import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ResultRendererProps } from './types';
import EmptyState from './EmptyResults';
import { Database } from 'lucide-react';

type LeakRow = Record<string, any>;
type Payload = {
  domain?: string;
  fromCache?: boolean;
  totalInserted?: number;
  data?: LeakRow[];
};

function normalize(results: any[]): Payload | null {
  if (!Array.isArray(results) || results.length === 0) return null;

  // Caso habitual: objeto con { data: [...] }
  const first = results[0];
  if (first && typeof first === 'object' && ('data' in first || 'domain' in first)) {
    const data = Array.isArray(first.data) ? first.data : [];
    return {
      domain: String(first.domain ?? ''),
      fromCache: Boolean(first.fromCache ?? false),
      totalInserted: Number(first.totalInserted ?? 0),
      data,
    };
  }

  // Fallback: una lista de filas directamente
  return { domain: '', fromCache: false, totalInserted: 0, data: results as LeakRow[] };
}

const HIDDEN_FIELDS = new Set([
  'domain',                 // redundante en filas
  'source_name', 'source_breach_date', // van en cabecera
]);

const PAGE_SIZE = 12;

const LeakedDataResults: React.FC<ResultRendererProps> = ({ results }) => {
  const { t } = useTranslation();

  // Hooks SIEMPRE arriba
  const [revealed, setRevealed] = React.useState<Record<number, boolean>>({});
  const [page, setPage] = React.useState(1);
  const payload = React.useMemo(() => normalize(results), [results]);

  const rows = payload?.data ?? [];
  const domain = payload?.domain || '';
  const fromCache = Boolean(payload?.fromCache);

  // Reajusta page si cambia el número de filas
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  React.useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const start = (page - 1) * PAGE_SIZE;
  const end = start + PAGE_SIZE;
  const pageRows = React.useMemo(() => rows.slice(start, end), [rows, start, end]);

  const toggle = React.useCallback((globalIndex: number) => {
    setRevealed(prev => ({ ...prev, [globalIndex]: !prev[globalIndex] }));
  }, []);

  const label = React.useCallback(
    (key: string) =>
      t(`leaksInfo.${key}`, key.replace(/_/g, ' ').replace(/\b\w/g, m => m.toUpperCase())),
    [t]
  );

  const toText = (v: any) => {
    if (v === null || v === undefined) return '';
    if (typeof v === 'string') return v;
    if (Array.isArray(v)) return v.join(', ');
    try { return JSON.stringify(v); } catch { return String(v); }
  };

  // ✅ ya podemos devolver vacío; los hooks se han llamado antes
  if (rows.length === 0) {
  return (
    <EmptyState
      title={t('tools.results_scan.leaked_data.empty')}
      subtitle={t('tools.results_scan.leaked_data.empty_hint')} // opcional
      icon={Database}
    />
  );
}

  const prevDisabled = page <= 1;
  const nextDisabled = page >= totalPages;

  return (
    <div className="w-full space-y-4">
      {/* Resumen */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-slate-700">
          <span className="font-semibold text-slate-900">{domain || t('tools.results_scan.leaked_data.domain')}</span>
          <span className="mx-1 text-slate-400">•</span>
          <span>{t('tools.results_scan.leaked_data.found_count', { count: rows.length })}</span>
        </div>
        <div className="flex items-center gap-2">
          {fromCache && (
            <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 bg-slate-50 text-slate-700 ring-slate-200">
              {t('tools.results_scan.leaked_data.from_cache')}
            </span>
          )}
          <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-slate-300 bg-slate-900 text-white">
            {rows.length}
          </span>
        </div>
      </div>

      {/* Grid de tarjetas (solo página actual) */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {pageRows.map((item, iOnPage) => {
          const globalIdx = start + iOnPage;

          const source = toText(item.source_name) || t('leaksInfo.unknown_source');
          const breach = toText(item.source_breach_date);

          const entries = Object
            .entries(item)
            .filter(([k, v]) => !HIDDEN_FIELDS.has(k) && toText(v))
            .map(([k, v]) => [label(k), toText(v)] as [string, string]);

          return (
            <div key={globalIdx} className="rounded-lg border border-slate-200 overflow-hidden bg-white flex flex-col">
              {/* Cabecera */}
              <div className="flex items-center justify-between px-3 py-2 bg-slate-900 text-white">
                <div className="font-semibold text-sm truncate">{source}</div>
                {breach ? (
                  <div className="text-xs opacity-85 ml-2 whitespace-nowrap">({breach})</div>
                ) : null}
              </div>

              {/* Tabla */}
              <div className="p-3 text-sm flex-1">
                {entries.length === 0 ? (
                  <div className="text-slate-500">{t('tools.results_scan.leaked_data.no_fields')}</div>
                ) : (
                  <table className="w-full">
                    <tbody className="divide-y divide-slate-200">
                      {entries.map(([k, v]) => (
                        <tr key={k} className="align-top">
                          <td className="py-1.5 pr-2 font-medium text-slate-700 w-36">{k}</td>
                          <td
                            className="py-1.5 text-slate-900 break-words"
                            style={{
                              filter: revealed[globalIdx] ? 'none' : 'blur(5px)',
                              transition: 'filter 120ms',
                            }}
                            title={revealed[globalIdx] ? undefined : (t('tools.results_scan.leaked_data.click_to_reveal') as string)}
                            onMouseDown={() => toggle(globalIdx)}
                            onMouseUp={() => toggle(globalIdx)}
                          >
                            {v}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Pie */}
              <div className="px-3 py-2 border-t border-slate-200 bg-slate-50 text-right">
                <button
                  type="button"
                  onClick={() => toggle(globalIdx)}
                  className="text-xs font-semibold rounded px-2 py-1 ring-1 ring-slate-300 hover:bg-white"
                >
                  {revealed[globalIdx]
                    ? t('tools.results_scan.leaked_data.hide')
                    : t('tools.results_scan.leaked_data.reveal')}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Paginación */}
      <div className="flex items-center justify-center gap-3 pt-2">
        <button
          type="button"
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={prevDisabled}
          className={`px-3 py-1.5 text-sm rounded ring-1 ${
            prevDisabled
              ? 'text-slate-400 ring-slate-200 cursor-not-allowed'
              : 'text-slate-700 ring-slate-300 hover:bg-white'
          }`}
        >
          {t('pagination.previous')}
        </button>

        <span className="text-sm text-slate-600">
          {t('tools.results_scan.leaked_data.page_of')} {page} {t('tools.results_scan.leaked_data.of')} {totalPages}
        </span>

        <button
          type="button"
          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          disabled={nextDisabled}
          className={`px-3 py-1.5 text-sm rounded ring-1 ${
            nextDisabled
              ? 'text-slate-400 ring-slate-200 cursor-not-allowed'
              : 'text-slate-700 ring-slate-300 hover:bg-white'
          }`}
        >
          {t('pagination.next')}
        </button>
      </div>
    </div>
  );
};

export default LeakedDataResults;
